'use client';

import { useMemo } from 'react';
import { Divider, Img, Text, VStack } from '@chakra-ui/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { Button, Input } from 'components';
import * as AppLayout from 'components/AppLayout';
import { validateRedeemPincode } from 'services/client/repositories/giftcard';
import { useAlertDialog } from 'context/alertDialog';

interface RedeemPincodeForm {
  pincode: string;
}

interface Props {
  onPincodeSubmit(): void;
  giftcardId: string;
  redeemToken: string;
  content: GiftcardRedeemPincodeContent;
}

export const GiftcardRedeemPincodeForm = (props: Props) => {
  const { onPincodeSubmit, giftcardId, redeemToken, content } = props;

  const {
    title,
    description,
    pincodeLabel,
    pincodeIsRequired,
    submitButton,
    invalidPincodeMessage,
    instructions,
    giftcardImageUrl,
  } = content;

  const { onOpenAlertDialog } = useAlertDialog();

  const validationSchema = useMemo(() => {
    return yup.object().shape({
      pincode: yup.string().label(pincodeLabel).required(pincodeIsRequired),
    });
  }, []);

  const { register, handleSubmit, formState } = useForm<RedeemPincodeForm>({
    defaultValues: { pincode: '' },
    resolver: yupResolver(validationSchema),
  });

  const onSubmit: SubmitHandler<RedeemPincodeForm> = async data => {
    try {
      const { validRedeemPagePin } = await validateRedeemPincode({
        giftcardId,
        redeemPagePin: data.pincode,
        redeemToken,
      });

      if (validRedeemPagePin) {
        onPincodeSubmit();

        return;
      }

      onOpenAlertDialog({
        content: invalidPincodeMessage,
        status: 'error',
      });
    } catch (error) {
      onOpenAlertDialog({
        content: invalidPincodeMessage,
        status: 'error',
      });
    }
  };

  return (
    <AppLayout.Root data-testid="giftcard-redeem-pincode-form">
      <AppLayout.DesktopView />

      <AppLayout.MainView>
        <AppLayout.MainContainer>
          <AppLayout.NetworkLogo />

          <AppLayout.GiftcardAmount visibility="hidden" />

          <AppLayout.Main>
            <VStack align="flex-start" mb="3rem">
              <Text fontSize="1.25rem" alignSelf="center">
                {title}
              </Text>

              <Text>{description}</Text>
            </VStack>

            <VStack
              as="form"
              w="full"
              align="flex-start"
              spacing="2.5rem"
              mb="2rem"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Input
                variant="flushed"
                label={pincodeLabel}
                fontFamily="code"
                {...register('pincode')}
                error={formState.errors.pincode}
                autoComplete="pincode"
              />

              <Button
                size="lg"
                w="full"
                variant="network"
                rounded="xl"
                type="submit"
                isLoading={formState.isSubmitting}
              >
                {submitButton}
              </Button>
            </VStack>

            <Divider borderStyle="dashed" />

            <VStack align="flex-start" spacing="2rem">
              <Text fontSize="0.875rem">{instructions}</Text>

              <Img
                src={giftcardImageUrl}
                maxW="25.3em"
                w="full"
                rounded="xl"
                shadow="dark-lg"
                alt="Pincode location"
              />
            </VStack>
          </AppLayout.Main>
        </AppLayout.MainContainer>
      </AppLayout.MainView>
    </AppLayout.Root>
  );
};
